import { useCallback } from 'react'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import {
  Connection,
  Transaction,
  VersionedTransaction,
  TransactionInstruction,
  TransactionMessage,
  PublicKey,
  AddressLookupTableAccount,
} from '@solana/web3.js'
import { newConnection, explorerUrl, friendlyTxError, type SubmitResult, type SignAndSubmitOptions } from './tx'

/**
 * Wallet-adapter `sendTransaction(tx, connection)` is the single most important
 * call for staying out of Phantom's "This dApp could be malicious" banner.
 *
 * Per Phantom's published guidance:
 *   1. Use `signAndSendTransaction` (the wallet method that signs and broadcasts
 *      atomically) — NOT `signTransaction` + manual `sendRawTransaction`.
 *      The "raw-sign-then-broadcast" pattern is what Phantom's scanner flags as
 *      suspicious because it can't vouch for what the dApp does between steps.
 *   2. Pre-simulate with `sigVerify: false` so Phantom can match its own
 *      simulation against a known-good outcome.
 *   3. Single signer per tx; ALTs for large txs (Jupiter v6 already complies).
 *
 * The wallet-adapter's `sendTransaction()` delegates to `signAndSendTransaction`
 * for wallets that expose it (Phantom always does). This is the recommended
 * pattern from `@solana/wallet-adapter-react`.
 */
export function useAdapterSignAndSubmit() {
  const { sendTransaction, publicKey, connected, connect, wallet } = useWallet()
  const { connection: adapterConnection } = useConnection()

  const signAndSubmit = useCallback(async (
    tx: Transaction | VersionedTransaction,
    opts: SignAndSubmitOptions = {},
  ): Promise<SubmitResult> => {
    // Silent autoconnect if the bridge hasn't completed yet.
    if ((!connected || !publicKey) && wallet) {
      try { await connect() } catch { /* fall through to the error below */ }
    }
    // publicKey from React state may be stale immediately after connect() — the
    // adapter's own field is updated synchronously when the extension resolves.
    const effectivePk = publicKey ?? wallet?.adapter.publicKey ?? null
    if (!sendTransaction || !effectivePk) {
      throw new Error('Wallet not connected — open Phantom/Solflare and retry')
    }

    // Prefer the app's own RPC (proxied through the backend with a paid API
    // key) over the public adapter RPC.
    const connection: Connection = newConnection()
    void adapterConnection

    let lastValidBlockHeight = opts.lastValidBlockHeight
    if (tx instanceof Transaction) {
      tx.feePayer = effectivePk
      const latest = await connection.getLatestBlockhash('confirmed')
      tx.recentBlockhash = latest.blockhash
      lastValidBlockHeight ??= latest.lastValidBlockHeight
    } else if (lastValidBlockHeight == null) {
      lastValidBlockHeight = (await connection.getLatestBlockhash('confirmed')).lastValidBlockHeight
    }

    // Pre-simulate unless the caller opted out. Phantom's scanner gives a
    // known-good outcome a much higher confidence score and is far less likely
    // to fire the "may be malicious" banner. We swallow RPC-side errors
    // (429/timeout) so infra wobble doesn't block legit flow, but propagate
    // real chain rejections.
    //
    // Callers may pass skipPreSimulation:true when the transaction contains
    // complex nested CPI chains (e.g. Jupiter Lend SDK initTick/Branch/Position
    // + Metaplex NFT mint) that produce false-positive ResultWithNegativeLamports
    // in simulation even with ample SOL. Phantom's own preflight is the gate.
    if (!opts.skipPreSimulation) {
      try {
        const sim = tx instanceof VersionedTransaction
          ? await connection.simulateTransaction(tx, { sigVerify: false, replaceRecentBlockhash: true })
          : await connection.simulateTransaction(tx as Transaction, undefined, false)
        if (sim.value.err) {
          const logs = (sim.value.logs ?? []).slice(-5).join(' | ')
          throw new Error(`Pre-simulation failed: ${JSON.stringify(sim.value.err)}${logs ? ` — ${logs}` : ''}`)
        }
      } catch (e: any) {
        if (String(e?.message ?? '').startsWith('Pre-simulation failed:')) throw e
        console.warn('[walletAdapter] simulation skipped:', e?.message ?? e)
      }
    }

    // Atomic sign+send through the wallet-adapter — this is what Phantom needs
    // to see. The adapter forwards to `signAndSendTransaction` internally.
    const signature = await sendTransaction(tx, connection, {
      skipPreflight: opts.skipPreflight ?? false,
      preflightCommitment: 'confirmed',
      maxRetries: 5,
    })

    const deadline = Date.now() + (opts.maxWaitSeconds ?? 90) * 1000
    const pollIntervalMs = opts.resendIntervalMs ?? 2000

    while (Date.now() < deadline) {
      try {
        const { value } = await connection.getSignatureStatuses([signature])
        const status = value?.[0]
        if (status?.err) {
          throw new Error(`Transaction failed on-chain: ${JSON.stringify(status.err)}`)
        }
        if (status?.confirmationStatus === 'confirmed' || status?.confirmationStatus === 'finalized') {
          return { signature, explorerUrl: explorerUrl(signature) }
        }
      } catch (e: any) {
        if (String(e?.message ?? '').includes('Transaction failed on-chain')) throw e
      }

      try {
        const height = await connection.getBlockHeight('confirmed')
        if (lastValidBlockHeight != null && height > lastValidBlockHeight) {
          throw new Error(`Transaction expired (blockheight ${height} > ${lastValidBlockHeight}). Check ${signature} on Solscan.`)
        }
      } catch (e: any) {
        if (String(e?.message ?? '').includes('expired')) throw e
      }

      await sleep(pollIntervalMs)
    }

    throw new Error(
      `Transaction was not confirmed within ${opts.maxWaitSeconds ?? 90} s. Signature ${signature} may still succeed — check Solscan.`,
    )
  }, [sendTransaction, publicKey, connected, adapterConnection, wallet, connect])

  /** Build a VersionedTransaction with ALTs — used for Jupiter Lend borrow. */
  const buildV0 = useCallback(async (
    ixs: TransactionInstruction[],
    lookupTables: AddressLookupTableAccount[] = [],
  ): Promise<VersionedTransaction> => {
    if ((!connected || !publicKey) && wallet) {
      try { await connect() } catch { /* fall through to the error below */ }
    }
    const effectivePk = publicKey ?? wallet?.adapter.publicKey ?? null
    if (!effectivePk) throw new Error('Wallet not connected — open Phantom/Solflare and retry')
    const connection = newConnection()
    const { blockhash } = await connection.getLatestBlockhash('confirmed')
    const msg = new TransactionMessage({
      payerKey: effectivePk,
      recentBlockhash: blockhash,
      instructions: ixs,
    }).compileToV0Message(lookupTables)
    return new VersionedTransaction(msg)
  }, [publicKey, connected, wallet, connect, adapterConnection])

  return {
    signAndSubmit,
    buildV0,
    publicKey,
    connected,
    connect,       // exposed so forms can trigger a connect on primary-CTA click
    wallet,        // adapter instance — null if none selected yet
    friendlyTxError,
  }
}

function sleep(ms: number) { return new Promise(r => setTimeout(r, ms)) }

/** Re-export so forms don't need two imports. */
export { friendlyTxError, type SubmitResult } from './tx'
export function makePublicKey(key: string | PublicKey): PublicKey {
  return key instanceof PublicKey ? key : new PublicKey(key)
}
